import { mapActions, mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'

import Menu from '@/components/Menu.vue'
import { app } from '@/config/index.js'
import { useCommonStore } from '@/stores/common.js'
import { useKycStore } from '@/stores/kyc.js'
import { useMemberStore } from '@/stores/member.js'
import { useAuthStore } from '@/stores/auth.js'
import { isApp, isTiketIosUserAgent, togglePullToRefresh } from '@/utils/browser.js'
import { isMobile } from '@/utils/responsive.js'

import BliketLoaderCustom from '@blibli/blibli-tiket.loader-custom/dist/loader-custom/blibli-tiket/LoaderCustom'
const DesktopBanner = defineAsyncComponent(() => import('@/components/banner/DesktopBanner.vue'))
const RestrictedPage = defineAsyncComponent(() => import('@/components/state/RestrictedPage.vue'))

export default {
  name: 'App',
  components: {
    BliketLoaderCustom,
    Menu,
    DesktopBanner,
    RestrictedPage
  },
  data () {
    return {
      isDesktop: false,
      isLoading: false
    }
  },
  computed: {
    ...mapState(useMemberStore, ['member']),
    ...mapState(useAuthStore, ['isRecoveryInProgress']),
    isIosFullscreen () {
      return isTiketIosUserAgent()
    },
    isMobile () {
      return isMobile()
    },
    isLoggedIn () {
      return !this.member.init
    },
    routePath () {
      return this.$route.path
    },
    isAffiliatePage () {
      const validRoutes = Object.values(app.routerName)
        .filter(name => name !== app.routerName.notFound)
      return validRoutes.includes(this.$route.name)
    },
    isShowMenu () {
      const isInHiddenRegex = app.hiddenMenuRegex.some(regex => regex.test(this.routePath))
      return this.isAffiliatePage &&
        !app.hiddenMenuPages.includes(this.routePath) &&
        !isInHiddenRegex &&
        this.isMobile &&
        this.isLoggedIn
    },
    isBlacklisted () {
      return this.member.status === 'BLACKLIST'
    },
    isBannerVisible () {
      return this.isAffiliatePage &&
        this.isDesktop &&
        !isApp() &&
        !app.hiddenDesktopBannerRegex.some(regex => regex.test(this.routePath))
    },
    isShowLoading () {
      return this.isLoading || this.isRecoveryInProgress
    }
  },
  methods: {
    ...mapActions(useCommonStore, ['getAffiliateConfigs']),
    ...mapActions(useKycStore, ['getKycStatus']),
    ...mapActions(useAuthStore, ['armRecoveryWatchdog']),
    getKycStatusSuccess () {
      this.isLoading = false
    },
    getKycStatusFailed () {
      this.isLoading = false
    },
    init () {
      togglePullToRefresh(false)
      this.isLoading = true
      this.getAffiliateConfigs({})
      this.getKycStatus({
        success: this.getKycStatusSuccess,
        fail: this.getKycStatusFailed
      })
    }
  },
  created () {
    // covers the case where this page instance loaded with recovery already
    // 'recovering' (hydrated from sessionStorage after a prior instance
    // triggered it but never got to resolve it — e.g. it was destroyed by the
    // bridge redirect's real navigation). That prior instance's own watchdog
    // died with it, so re-arm one here — a no-op unless recovery is actually
    // in progress — to guarantee this instance still bounds the wait instead
    // of spinning forever.
    this.armRecoveryWatchdog()
    this.init()
  },
  mounted () {
    this.isDesktop = !this.isMobile
  },
  unmounted () {
    togglePullToRefresh(true)
  }
}
